import path from 'path';
import {
  ElementDropdownListIcon as DropdownListIcon,
  ElementDropdownDefaultIcon as DropdownIcon,
  AddPanelDropdown64Icon,
} from '@packages/systems/spring';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import DropdownLink from './DropdownLink';
import DropdownList from './DropdownList';
import DropdownToggle from './DropdownToggle';
import DropdownWrapper from './DropdownWrapper';
import AddDropdownLink from './dataTypes/AddDropdownLink';
import DropdownWrapperDataType from './dataTypes/DropdownWrapperDataType';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, ElementPreset, Component} from '@packages/systems/wfdl';

import {
  EElement,
  EText,
  ERecord,
  EBoolean,
  EList,
  ELiteral,
  ENumber,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {MENU_STATE_HIDE} from '@packages/utilities/expression-path-constants';

const DropdownAddPanelIcon = wrapAddPanelIcon(AddPanelDropdown64Icon);

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    Dropdown: DropdownIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    AddPanelDropdown: DropdownAddPanelIcon,
    DropdownList: DropdownListIcon,
  },

  atoms: {
    DropdownLink,
    DropdownList,
    DropdownToggle,
    DropdownWrapper,
  },

  dataTypes: {
    AddDropdownLink,
    DropdownWrapper: DropdownWrapperDataType,
  },

  elementPresets: {
    DropdownWrapper: ElementPreset.create({
      factory: EElement({
        id: 'Dropdown Wrapper',
        type: qualify(
          Plugins.name('Dropdown'),
          Component.name('DropdownWrapper')
        ),
        data: ERecord({
          tag: EEnum('div'),
          dropdown: ERecord({
            type: EText('wrapper'),
          }),
          temp: ERecord({
            dropdown: ERecord({
              menu: EEnum(MENU_STATE_HIDE),
            }),
          }),
          attr: ERecord({
            'data-hover': EBoolean(false),
            'data-delay': ENumber(0),
          }),
          children: EList([
            EElement({
              id: 'Dropdown Toggle',
              type: qualify(
                Plugins.name('Dropdown'),
                Component.name('DropdownToggle')
              ),
              data: ERecord({
                tag: EEnum('div'),
                dropdown: ERecord({
                  type: EText('toggle'),
                }),
                children: EList([
                  EElement({
                    id: 'Icon',
                    type: qualify(Plugins.name('Icon'), Component.name('Icon')),
                    data: ERecord({
                      widget: ERecord({
                        type: EText('icon'),
                        icon: EText('dropdown-toggle'),
                      }),
                      children: EList([]),
                    }),
                  }),
                  EElement({
                    id: 'Block',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Block')
                    ),
                    data: ERecord({
                      tag: EEnum('div'),
                      text: EBoolean(true),
                      children: EList([
                        EElement({
                          id: 'String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Dropdown'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            EElement({
              id: 'Dropdown List',
              type: qualify(
                Plugins.name('Dropdown'),
                Component.name('DropdownList')
              ),
              data: ERecord({
                tag: EEnum('nav'),
                dropdown: ERecord({
                  type: EText('list'),
                }),
                children: EList([
                  EElement({
                    id: 'Dropdown Link',
                    type: qualify(
                      Plugins.name('Dropdown'),
                      Component.name('DropdownLink')
                    ),
                    data: ERecord({
                      dropdown: ERecord({
                        type: EText('link'),
                      }),
                      link: ELiteral({
                        name: qualify(Plugins.name('Basic'), typeName('Link')),
                        value: {
                          mode: 'external',
                          url: '#',
                        },
                      }),
                      eventIds: EList([]),
                      children: EList([
                        EElement({
                          id: 'String 2',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Link 1'),
                        }),
                      ]),
                    }),
                  }),
                  EElement({
                    id: 'Dropdown Link 2',
                    type: qualify(
                      Plugins.name('Dropdown'),
                      Component.name('DropdownLink')
                    ),
                    data: ERecord({
                      dropdown: ERecord({
                        type: EText('link'),
                      }),
                      link: ELiteral({
                        name: qualify(Plugins.name('Basic'), typeName('Link')),
                        value: {
                          mode: 'external',
                          url: '#',
                        },
                      }),
                      eventIds: EList([]),
                      children: EList([
                        EElement({
                          id: 'String 3',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Link 2'),
                        }),
                      ]),
                    }),
                  }),
                  EElement({
                    id: 'Dropdown Link 3',
                    type: qualify(
                      Plugins.name('Dropdown'),
                      Component.name('DropdownLink')
                    ),
                    data: ERecord({
                      dropdown: ERecord({
                        type: EText('link'),
                      }),
                      link: ELiteral({
                        name: qualify(Plugins.name('Basic'), typeName('Link')),
                        value: {
                          mode: 'external',
                          url: '#',
                        },
                      }),
                      eventIds: EList([]),
                      children: EList([
                        EElement({
                          id: 'String 4',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Link 3'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      icon: {
        small: qualify(Plugins.name('Dropdown'), iconName('Dropdown')),
        medium: qualify(Plugins.name('Dropdown'), iconName('AddPanelDropdown')),
      },
      label: 'Dropdown',
      helpData: {
        description: [
          'Dropdown lists give your site visitors different link options. For example, add a “resources” dropdown in your Navbar that lists your "about" and "blog" pages.',
        ],
        tip: 'Get creative with trigger text, hover settings, and more.',
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/dropdown.788a1f29f6.mp4',
      },
      category: 'Other',
      addTabIndex: 4,
    }),
  },

  siteModules: [
    {
      filePaths: [path.resolve(__dirname, 'webflow-dropdown.js')],
      bundleIf: {
        type: 'node',
        test: ({data = {}}) => data.dropdown !== undefined,
      },
    },
  ],
});
